import {
  AtomicBlockUtils,
  Editor as BaseEditor,
  ContentState,
  DefaultDraftBlockRenderMap,
  EditorState,
  RawDraftContentState,
  RichUtils,
  SelectionState,
  convertFromRaw,
  convertToRaw,
} from 'draft-js';
import 'draft-js/dist/Draft.css';
import { Map } from 'immutable';
import { ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { addFile, openDb } from '~/components/upload/db';
import { createUppy } from '~/components/upload2/uppy';
import { Toolbar } from './Toolbar';
import { createBlockRenderer } from './util';

type Props = {
  content?: RawDraftContentState | null;
  onUpdate: (rawState: RawDraftContentState) => void;
  placeholder?: string;
  savedIndicator?: ReactNode;
};

/**
 * Validates that a value is a valid RawDraftContentState that can be safely
 * passed to convertFromRaw. A valid state must have:
 * - blocks: a non-empty array of block objects (each with key, text, type, depth,
 *   inlineStyleRanges, entityRanges, data)
 * - entityMap: an object (can be empty)
 */
const isValidRawDraftContentState = (
  value: unknown
): value is RawDraftContentState => {
  if (!value || typeof value !== 'object') return false;

  const raw = value as Record<string, unknown>;

  // Must have blocks array
  if (!Array.isArray(raw.blocks) || raw.blocks.length === 0) return false;

  // Each block must have required properties
  const hasValidBlocks = raw.blocks.every((block: unknown) => {
    if (!block || typeof block !== 'object') return false;
    const b = block as Record<string, unknown>;

    // Validate basic required properties
    if (
      typeof b.key !== 'string' ||
      typeof b.text !== 'string' ||
      typeof b.type !== 'string'
    ) {
      return false;
    }

    // Validate depth is a number
    if (typeof b.depth !== 'number') return false;

    // Validate inlineStyleRanges is an array (can be empty)
    if (!Array.isArray(b.inlineStyleRanges)) return false;
    // Validate each inlineStyleRange entry has correct shape
    const hasValidInlineStyleRanges = b.inlineStyleRanges.every(
      (range: unknown) => {
        if (!range || typeof range !== 'object') return false;
        const r = range as Record<string, unknown>;
        return (
          typeof r.offset === 'number' &&
          typeof r.length === 'number' &&
          typeof r.style === 'string'
        );
      }
    );
    if (!hasValidInlineStyleRanges) return false;

    // Validate entityRanges is an array (can be empty)
    if (!Array.isArray(b.entityRanges)) return false;
    // Validate each entityRange entry has correct shape
    const hasValidEntityRanges = b.entityRanges.every((range: unknown) => {
      if (!range || typeof range !== 'object') return false;
      const r = range as Record<string, unknown>;
      return (
        typeof r.offset === 'number' &&
        typeof r.length === 'number' &&
        typeof r.key === 'number'
      );
    });
    if (!hasValidEntityRanges) return false;

    // Validate data is an object (not null)
    if (typeof b.data !== 'object' || b.data === null) return false;

    return true;
  });
  if (!hasValidBlocks) return false;

  // Must have entityMap object
  if (typeof raw.entityMap !== 'object' || raw.entityMap === null) return false;

  return true;
};

export const Editor = ({
  content,
  placeholder,
  onUpdate,
  savedIndicator,
}: Props) => {
  // Support checklist blocks
  const blockRenderMap = Map({
    Checklist: {
      element: 'li',
      wrapper: {
        type: 'ul',
        props: { className: 'checklist' },
      },
      // type: ''
      mutability: 'MUTABLE',
    },
  });

  const extendedBlockRenderMap =
    DefaultDraftBlockRenderMap.merge(blockRenderMap);

  // const LINK_REGEX = RegExp(/^(ftp|http|https):\/\/[^ "]+$/);

  // const decorator = new CompositeDecorator([
  //   {
  //     strategy: findLinkEntities,
  //     component: Link,
  //   },
  //   {
  //     strategy: linkStrategy,
  //     component: PastedLink,
  //   },
  // ]);

  // Editor should be set to read only while using the mouse
  const [readOnly] = useState(false);

  // Track number of files currently uploading for visual feedback
  const [uploadingCount, setUploadingCount] = useState(0);

  // Track the previous content to detect actual content changes vs selection-only changes
  // This prevents triggering onUpdate when the user just clicks in the field or moves the cursor
  const previousContentRef = useRef<ContentState | null>(null);

  // Track pending content for unmount flush - prevents data loss if user navigates away
  // while debounce timer is still running
  const pendingContentRef = useRef<ContentState | null>(null);

  // Keep a fresh reference to onUpdate for the unmount effect (avoids stale closure)
  const onUpdateRef = useRef(onUpdate);
  onUpdateRef.current = onUpdate;

  const [editorState, setEditorState] = useState(() => {
    if (isValidRawDraftContentState(content)) {
      const initialState = EditorState.createWithContent(
        convertFromRaw(content)
      );
      // Initialize the ref with the initial content to prevent immediate onUpdate call
      previousContentRef.current = initialState.getCurrentContent();
      return initialState;
    }
    const emptyState = EditorState.createEmpty();
    // Also initialize ref for empty state to prevent unnecessary onUpdate call on mount
    previousContentRef.current = emptyState.getCurrentContent();
    return emptyState;
  });

  // Ref to access current editor state without adding it to effect dependencies
  const editorStateRef = useRef(editorState);
  editorStateRef.current = editorState;

  // Effect to sync editor state when content prop changes
  // This handles external updates to the content (e.g., form reset, navigating to different entity)
  // IMPORTANT: Skip updates entirely if editor has focus - user is actively editing
  useEffect(() => {
    if (!isValidRawDraftContentState(content)) {
      return;
    }

    // NEVER update while editor has focus - this prevents any jumping during editing
    if (editorStateRef.current.getSelection().getHasFocus()) {
      return;
    }

    // Compare structural raw content to avoid unnecessary updates even when not focused
    // This catches formatting-only changes that plain text comparison would miss
    const rawCurrent = JSON.stringify(
      convertToRaw(editorStateRef.current.getCurrentContent())
    );
    // content is already RawDraftContentState, so we can stringify it directly
    const rawNew = JSON.stringify(content);

    // Skip update if structural content is the same
    if (rawCurrent === rawNew) {
      return;
    }

    const newEditorState = EditorState.createWithContent(
      convertFromRaw(content)
    );
    const newContent = newEditorState.getCurrentContent();

    // Update previousContentRef to mark this as the "known" content
    // This prevents the onUpdate effect from firing for server-originated changes
    previousContentRef.current = newContent;

    setEditorState(newEditorState);
  }, [content]);

  // Unmount-only effect: flush any pending content to prevent data loss
  // This handles the case where user navigates away while the debounce timer is running
  // Uses empty dependency array so cleanup only runs on actual unmount, not re-renders
  useEffect(() => {
    return () => {
      // Flush pending content on unmount if it hasn't been saved yet
      if (
        pendingContentRef.current &&
        pendingContentRef.current !== previousContentRef.current
      ) {
        onUpdateRef.current(convertToRaw(pendingContentRef.current));
      }
    };
  }, []);

  // Effect to call onUpdate only when the actual content changes, not selection/focus changes
  // Draft.js editorState contains both content AND selection state, so we need to compare
  // the content specifically to avoid unnecessary updates on click/focus events
  // Debounces for 1 second to avoid excessive parent updates while typing
  useEffect(() => {
    const currentContent = editorState.getCurrentContent();

    // Skip if content hasn't actually changed (just selection/focus changed)
    // This is a reference comparison - Draft.js returns the same ContentState object
    // when only selection changes, but a new object when content changes
    if (previousContentRef.current === currentContent) {
      return;
    }

    // Track pending content for unmount flush
    pendingContentRef.current = currentContent;

    const handle = setTimeout(() => {
      onUpdate(convertToRaw(currentContent));
      previousContentRef.current = currentContent;
      // Clear pending after successful save
      pendingContentRef.current = null;
    }, 1000);

    // Cleanup: clear timeout if effect re-runs
    // Note: We intentionally do NOT flush here - only on actual unmount (handled above)
    return () => {
      clearTimeout(handle);
    };
  }, [editorState, onUpdate]);

  const handleKeyCommand = (command: string, editorState: EditorState) => {
    const newState = RichUtils.handleKeyCommand(editorState, command);
    if (newState) {
      setEditorState(newState);
      return 'handled';
    }

    return 'not-handled';
  };

  const handleChange = (editorState: EditorState) => {
    setEditorState(editorState);
  };

  const customBlockRenderer = createBlockRenderer(editorState, handleChange);

  // const confirmLink = (urlValue: string) => {
  //   const contentState = editorState.getCurrentContent();
  //   const contentStateWithEntity = contentState.createEntity(
  //     'LINK',
  //     'MUTABLE',
  //     { url: urlValue }
  //   );
  //   const entityKey = contentStateWithEntity.getLastCreatedEntityKey();
  //   const newEditorState = EditorState.set(editorState, {
  //     currentContent: contentStateWithEntity,
  //   });
  //   setEditorState(
  //     RichUtils.toggleLink(
  //       newEditorState,
  //       newEditorState.getSelection(),
  //       entityKey
  //     )
  //   );
  // };

  // const removeLink = () => {
  //   const selection = editorState.getSelection();
  //   if (!selection.isCollapsed()) {
  //     setEditorState(RichUtils.toggleLink(editorState, selection, null));
  //   }
  // };

  // Support Links

  // function findLinkEntities(
  //   contentBlock: ContentBlock,
  //   callback: (start: number, end: number) => void,
  //   contentState: ContentState
  // ) {
  //   contentBlock.findEntityRanges((character) => {
  //     const entityKey = character.getEntity();
  //     return (
  //       entityKey !== null &&
  //       contentState.getEntity(entityKey).getType() === 'LINK'
  //     );
  //   }, callback);
  // }

  // function linkStrategy(
  //   contentBlock: ContentBlock,
  //   callback: (start: number, end: number) => void
  // ) {
  //   findWithRegex(LINK_REGEX, contentBlock, callback);
  // }

  // function findWithRegex(
  //   regex: RegExp,
  //   contentBlock: ContentBlock,
  //   callback: (start: number, end: number) => void
  // ) {
  //   const text = contentBlock.getText();
  //   let matchArr, start;
  //   if ((matchArr = regex.exec(text)) !== null) {
  //     start = matchArr.index;
  //     callback(start, start + matchArr[0].length);
  //   }
  // }

  const styleMap = {
    STRIKETHROUGH: {
      textDecoration: 'line-through',
    },
  };

  const addImage = useCallback((data: { id: string; baseUrl: string }) => {
    setEditorState((currentState) => {
      const contentState = currentState.getCurrentContent();
      const contentStateWithEntity = contentState.createEntity(
        'IMAGE',
        'IMMUTABLE',
        data
      );

      const entityKey = contentStateWithEntity.getLastCreatedEntityKey();
      const newEditorState = EditorState.set(currentState, {
        currentContent: contentStateWithEntity,
      });

      return AtomicBlockUtils.insertAtomicBlock(newEditorState, entityKey, ' ');
    });
  }, []);

  const uploadFiles = useCallback(
    async (files: File[]) => {
      if (!files.length) return;

      // Upload files sequentially to avoid overwhelming the server
      for (const file of files) {
        if (!(file instanceof File)) continue;

        // Create a new Uppy instance for each file to avoid conflicts
        // Disable autoProceed so we have full control over when upload starts
        const uppy = createUppy(true); // imagesOnly = true

        // Disable autoProceed to prevent race condition where upload starts
        // before we call upload() - this ensures upload() returns proper results
        uppy.setOptions({ autoProceed: false });

        // Track upload in progress for visual feedback in the editor
        setUploadingCount((prev) => prev + 1);

        try {
          // Add file to Uppy
          uppy.addFile({
            name: file.name,
            type: file.type,
            data: file,
            source: 'Local',
          });

          // Now manually trigger the upload and await the result
          const result = await uppy.upload();

          if (result && result.successful && result.successful.length > 0) {
            const uploadResult = result.successful[0];
            const body = uploadResult.response?.body;

            if (body?.id && body?.url) {
              // Save the file blob to IndexedDB so Media component can display it
              // This allows immediate display without waiting for server URL
              try {
                const db = await openDb();
                await addFile(db, file, body.id);
              } catch (dbError) {
                // Non-fatal: image will still work via remote URL fallback
                console.warn('Failed to cache image locally:', dbError);
              }

              addImage({ id: body.id, baseUrl: body.url });
            } else {
              throw new Error(
                'Upload response missing required fields (id or url)'
              );
            }
          } else if (result && result.failed && result.failed.length > 0) {
            // Check for specific error details from failed uploads
            const failedFile = result.failed[0];
            const errorMessage =
              failedFile.error || 'Upload failed: unknown error';
            throw new Error(errorMessage);
          } else {
            throw new Error('Upload failed: no successful uploads');
          }

          // Decrement uploading count on success
          setUploadingCount((prev) => prev - 1);

          // Clean up Uppy instance after upload
          uppy.destroy();
        } catch (error) {
          console.error('Image upload error:', error);
          const errorMessage =
            error instanceof Error
              ? error.message
              : 'Failed to upload image. Please try again.';

          toast.error(`Failed to upload ${file.name}: ${errorMessage}`);

          // Decrement uploading count on error
          setUploadingCount((prev) => prev - 1);

          // Clean up Uppy instance on error
          uppy.destroy();
        }
      }
    },
    [addImage]
  );

  const handleDroppedFiles = (selection: SelectionState, files: File[]) => {
    if (files.length && files[0] instanceof File) {
      uploadFiles(files);
      return 'handled';
    }

    return 'not-handled';
  };

  const editorRef = useRef<HTMLDivElement>(null);

  // Handle paste events to support pasting images
  useEffect(() => {
    const editorElement = editorRef.current;
    if (!editorElement) return;

    const handlePaste = async (e: ClipboardEvent) => {
      const items = e.clipboardData?.items;
      if (!items) return;

      const imageFiles: File[] = [];
      let hasTextContent = false;

      // Extract image files from clipboard and detect text content
      for (let i = 0; i < items.length; i++) {
        const item = items[i];

        if (item.kind === 'string') {
          hasTextContent = true;
        }

        // Check if the item is a file and has an image MIME type
        // This matches the file picker behavior which accepts all image/* types
        if (item.kind === 'file' && item.type.startsWith('image/')) {
          const file = item.getAsFile();
          if (file) {
            imageFiles.push(file);
          }
        }
      }

      // Upload images; only block default paste when no text accompanies them
      if (imageFiles.length > 0) {
        if (!hasTextContent) {
          e.preventDefault();
          e.stopPropagation();
        }
        await uploadFiles(imageFiles);
      }
    };

    editorElement.addEventListener('paste', handlePaste);
    return () => {
      editorElement.removeEventListener('paste', handlePaste);
    };
  }, [uploadFiles]);

  const hasSavedIndicator = savedIndicator !== undefined;

  return (
    <div
      ref={editorRef}
      className={`RichEditor-root mb-3 mt-1.5 rounded border border-grey-20 focus-within:border-grey-40 ${
        hasSavedIndicator ? 'relative' : ''
      }`}
    >
      <Toolbar editorState={editorState} setEditorState={setEditorState}>
        <Toolbar.Bold />
        <Toolbar.Italic />
        <Toolbar.Underline />
        <Toolbar.Strikethrough />
        <Toolbar.Checklist />
        <Toolbar.OrderedList />
        <Toolbar.UnorderedList />
        <Toolbar.Image onAddImage={uploadFiles} />
        {/* <Toolbar.Link /> */}
      </Toolbar>
      <div
        className={`RichEditor-editor mb-6 mt-4 pl-3 ${
          hasSavedIndicator ? 'pb-8' : 'pb-2'
        }`}
      >
        <BaseEditor
          placeholder={placeholder}
          editorState={editorState}
          customStyleMap={styleMap}
          blockRenderMap={extendedBlockRenderMap}
          blockRendererFn={customBlockRenderer}
          handleKeyCommand={handleKeyCommand}
          handleDroppedFiles={handleDroppedFiles}
          onChange={handleChange}
          readOnly={readOnly}
        />
        {uploadingCount > 0 && (
          <div className='text-grey-60 flex items-center gap-2 border-t border-grey-20 px-3 py-2 text-sm'>
            <div className='h-4 w-4 animate-spin rounded-full border-2 border-grey-30 border-t-primary' />
            <span>
              Uploading {uploadingCount} image{uploadingCount > 1 ? 's' : ''}...
            </span>
          </div>
        )}
      </div>
      {hasSavedIndicator && (
        <div className='absolute bottom-2 left-3'>{savedIndicator}</div>
      )}
    </div>
  );
};
